import { JSX, useCallback, useEffect, useState, Dispatch, SetStateAction } from 'react';
import { Menu } from 'antd';
import {
    CarryOutOutlined,
    CommentOutlined,
    DashboardOutlined,
    HourglassOutlined,
    NotificationOutlined,
    SettingOutlined,
    TeamOutlined,
    UploadOutlined,
    CreditCardOutlined,
    HomeOutlined,
    MessageOutlined,
    ArrowRightOutlined,
    ArrowLeftOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import isNil from 'lodash/isNil';
import { useSelector } from 'react-redux';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { Features } from '@copilot/data/responses/interface';
import { useCopilotTheme } from '@copilot/teams/copilot-theme-provider';
import { InboxDisplayType } from '@copilot/data/requests/models';
import { MenuInfo } from 'rc-menu/lib/interface';
import useTeamsPageSideBarContext from '@copilot/teams/src/useTeamsPageSideBarContext';
import { MEETING_BOOKED_COUNT_REFRESH_CALLBACK } from '@copilot/teams/src/context/constants';
import { IMeetingStatus, useDetectedMeetingsCountQuery } from '@copilot/data/graphql/_generated';
import debounce from 'lodash/debounce';
import { useFeatureToggle } from '@copilot/common/hooks/feature';
import FeServerSettingsManager from '@copilot/data/managers/feServerSettings';
import { useFetch } from '@copilot/common/hooks/common';
import { BASE_APP_PATHS } from '../../routerConstants';
import {
    getInboxNotificationCount,
    getLabelWithNotificationCount,
    getOpenAndActiveKeys,
    getOutboxNotificationCountAndColor,
} from './sideBarHelpers';
import styles from './sideBar.module.less';
import { useSidebarNotifications } from './useSideBarNotifications';
import { MenuItemType } from '@copilot/common/types/antd';
import { useActiveAccount } from '@copilot/common/hooks/account';
import { RocketOutlined as RocketOutlinedIcon } from '@ant-design/icons/lib/icons';
import { Config } from '@copilot/common/config';
import isEmpty from 'lodash/isEmpty';

export type SideBarProps = {
    isCollapsed: boolean;
    setIsCollapsed: Dispatch<SetStateAction<boolean>>;
};

/**
 * Side bar for the prospecting app
 * @returns
 */
export function BaseAppSideBar({ isCollapsed, setIsCollapsed }: SideBarProps): JSX.Element {
    const navigate = useNavigate();
    const location = useLocation();
    const { openKeys, activeKeys } = getOpenAndActiveKeys(location.pathname);
    const notifications = useSidebarNotifications();
    const theme = useCopilotTheme();

    const [detectedMeetingCount, setDetectedMeetingCount] = useState<number>(0);
    const isSelfManagedAdminFeatureEnabled = useFeatureToggle(Features.SelfManagedAdminFeature);
    const [serverSettings, fetchServerSettings] = useFetch(
        FeServerSettingsManager.getServerSettings
    );

    useEffect(() => {
        if (isSelfManagedAdminFeatureEnabled) {
            // Fetch the server settings from the backend
            fetchServerSettings();
        }
    }, [isSelfManagedAdminFeatureEnabled, fetchServerSettings]);

    function onRefreshMeetingsBookedCount() {
        //first optimistically assume we successfully decremented the count (eg a detected meeting was confirmed or denied)

        //then ask the server for the actual count and update if successful/valid
        refetchCount().then((res) => {
            const newCount = res.data?.campaignConnectionsByMeetings?.count;
            if (typeof newCount === 'number') {
                setDetectedMeetingCount(newCount);
            }
        });
    }

    const { refetch: refetchCount } = useDetectedMeetingsCountQuery({
        variables: {
            input: {
                meeting: { status: IMeetingStatus.Detected },
                pagination: { page: 0, pageSize: 0 },
            },
        },
        onCompleted(data) {
            const newCount = data?.campaignConnectionsByMeetings?.count;
            if (typeof newCount === 'number') {
                setDetectedMeetingCount(newCount);
            }
        },
    });

    const debouncedOnRefetchMeetingsBookedCount = useCallback(
        debounce(onRefreshMeetingsBookedCount, 200),
        [refetchCount]
    );

    const { registerCallback } = useTeamsPageSideBarContext();

    useEffect(() => {
        registerCallback(MEETING_BOOKED_COUNT_REFRESH_CALLBACK, () => {
            debouncedOnRefetchMeetingsBookedCount();
        });
    }, []);

    // Get members
    const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);

    let sysAdminItems: MenuItemType[] = [];
    if (activeMember?.isSysAdmin === true) {
        sysAdminItems = [
            {
                label: 'Admin Hub',
                icon: <DashboardOutlined />,
                key: BASE_APP_PATHS.AdminHub,
            },
        ];
    }

    let orgAdminItems: MenuItemType[] = [];
    if (activeMember?.isOrgAdmin === true) {
        orgAdminItems = [
            {
                label: 'Home',
                icon: <HomeOutlined />,
                key: BASE_APP_PATHS.OrganizationHub,
            },
        ];
    }

    const notificationDisplayColor: string = theme['@notification-display-color'];
    const actionsCount = activeMember?.isOrgAdmin
        ? getInboxNotificationCount(InboxDisplayType.Email, notifications.Actions)
        : 0;
    const memberActionsCount = getInboxNotificationCount(
        InboxDisplayType.Email,
        notifications.MemberActions
    );
    const inboxItem = {
        label: getLabelWithNotificationCount(
            'Inbox',
            memberActionsCount ?? 0,
            notificationDisplayColor
        ),
        icon: <MessageOutlined />,
        key: BASE_APP_PATHS.Inbox,
    };

    const teamInboxItem = {
        label: getLabelWithNotificationCount(
            'Team Inbox',
            actionsCount ?? 0,
            notificationDisplayColor
        ),
        icon: <CommentOutlined />,
        key: BASE_APP_PATHS.TeamInbox,
    };

    const { data: currentAccount } = useActiveAccount();
    const isTeamInboxShown = (currentAccount?.isTeamAccount ?? false) && activeMember?.isOrgAdmin;

    const inboxItems = isTeamInboxShown ? [inboxItem, teamInboxItem] : [inboxItem];

    const outboxCountData = getOutboxNotificationCountAndColor(notifications, {
        pendingColor: notificationDisplayColor,
        errorColor: '#fbc02d',
    });

    const items = [
        {
            key: 'collapse',
            label: isCollapsed ? 'Expand' : 'Collapse',
            icon: isCollapsed ? <ArrowRightOutlined /> : <ArrowLeftOutlined />,
            onClick: () => setIsCollapsed((prev) => !prev),
        },
        ...sysAdminItems,
        ...orgAdminItems,
        {
            label: 'Campaigns',
            key: BASE_APP_PATHS.AllCampaigns,
            icon: <NotificationOutlined />,
            children: undefined,
        },
        ...inboxItems,
        {
            label: isNil(outboxCountData)
                ? 'Outbox'
                : getLabelWithNotificationCount(
                      'Outbox',
                      outboxCountData.count,
                      outboxCountData.color
                  ),
            icon: <HourglassOutlined />,
            key: BASE_APP_PATHS.Outbox,
        },
        {
            label: 'Sent',
            icon: <UploadOutlined />,
            key: BASE_APP_PATHS.Sent,
        },
        {
            label: 'Connections',
            icon: <TeamOutlined />,
            key: BASE_APP_PATHS.Connections,
        },
        {
            label: getLabelWithNotificationCount(
                'Meetings',
                detectedMeetingCount,
                notificationDisplayColor
            ),
            icon: <CarryOutOutlined />,
            key: BASE_APP_PATHS.MeetingsBooked,
        },
        {
            label: 'Settings',
            icon: <SettingOutlined />,
            key: BASE_APP_PATHS.Settings,
        },
        // Add Billing menu item if SelfManagedAdminFeature is enabled and we have a URL
        ...(isSelfManagedAdminFeatureEnabled &&
        !isNil(serverSettings?.data?.stripeCustomerPortalUrl)
            ? [
                  {
                      key: BASE_APP_PATHS.Billing,
                      onClick: () => {
                          window.open(serverSettings?.data?.stripeCustomerPortalUrl, '_blank');
                      },
                      icon: <CreditCardOutlined />,
                      label: 'Billing',
                  },
              ]
            : []),
        ...(Config.isAgency && !isEmpty(Config.referralURL.trim())
            ? [
                  {
                      key: BASE_APP_PATHS.Referral,
                      onClick: () => {
                          window.open(Config.referralURL);
                      },
                      icon: <RocketOutlinedIcon />,
                      label: 'Referral payout',
                  },
              ]
            : []),
    ].filter((item) => !isNil(item));

    function handleMenuItemClick(item: MenuInfo) {
        if (item.key !== BASE_APP_PATHS.CreateCampaign && item.key !== 'collapse') {
            const shouldOpenNewTab = item.domEvent.ctrlKey || item.domEvent.metaKey;
            shouldOpenNewTab ? window.open(item.key) : navigate(item.key);
        }
    }

    return (
        <Menu
            items={items}
            mode="inline"
            onClick={handleMenuItemClick}
            className={styles.sidebar}
            selectedKeys={activeKeys}
            defaultOpenKeys={openKeys}
        />
    );
}
